import React from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sun, Leaf, IndianRupee } from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1777571051052-6ad3c7031811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHBhbmVscyUyMHJvb2YlMjBtb2Rlcm58ZW58MHx8fHwxNzc3NzAzNjg5fDA&ixlib=rb-4.1.0&q=85";

export default function Hero({ onQuote, onCalc }) {
  const { t } = useTranslation();
  const stats = [
    { k: t("hero.stat1_k"), v: t("hero.stat1_v") },
    { k: t("hero.stat2_k"), v: t("hero.stat2_v") },
    { k: t("hero.stat3_k"), v: t("hero.stat3_v") },
  ];
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="hero-radial relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10 md:pt-20 pb-16 md:pb-28 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white border border-green-200 text-green-800 rounded-full px-3 py-1.5 text-xs font-semibold tracking-wide shadow-sm">
            <Leaf className="w-3.5 h-3.5" />
            {t("hero.badge")}
          </div>
          <h1 className="mt-5 font-display font-black text-[44px] sm:text-5xl lg:text-6xl leading-[1.02] text-stone-900">
            {t("hero.title_pre")}{" "}
            <span className="hand-underline text-green-700">{t("hero.title_highlight")}</span>
            <br className="hidden sm:block" /> {t("hero.title_post1")}{" "}
            <span className="relative inline-block">
              <span className="relative z-10">{t("hero.title_post2")}</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-yellow-300/70 -z-0 rounded-sm" />
            </span>
          </h1>
          <p className="mt-6 text-stone-600 text-lg leading-relaxed max-w-xl">
            {t("hero.subtitle_pre")}{" "}
            <span className="font-semibold text-stone-800">PM Surya Ghar</span>{" "}
            {t("hero.subtitle_and")}{" "}
            <span className="font-semibold text-stone-800">PM-KUSUM</span>{" "}
            {t("hero.subtitle_post")}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={onQuote}
              data-testid="hero-cta-quote"
              className="btn-premium rounded-full bg-green-600 hover:bg-green-700 text-white h-12 px-7 font-semibold"
            >
              {t("hero.cta_quote")}
              <ArrowRight className="ml-1.5 w-4 h-4" />
            </Button>
            <Button
              onClick={onCalc}
              variant="outline"
              data-testid="hero-cta-calc"
              className="rounded-full h-12 px-7 border-stone-300 bg-white/70 backdrop-blur text-stone-800 font-semibold hover:bg-white hover:border-green-400 transition"
            >
              {t("hero.cta_calc")}
            </Button>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl">
            {stats.map((s) => (
              <div
                key={s.v}
                className="rounded-2xl bg-white/70 backdrop-blur border border-stone-200 p-4"
              >
                <div className="digit-stat font-display font-extrabold text-2xl text-green-700">
                  {s.k}
                </div>
                <div className="text-[12px] text-stone-500 leading-snug mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative mx-auto w-full max-w-md aspect-[4/5] rounded-[32px] overflow-hidden shadow-2xl border border-stone-200 float-slow">
            <img
              src={HERO_IMG}
              alt="Solar rooftop"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg">
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <Sun className="w-4 h-4 text-yellow-500 sun-pulse" /> {t("hero.live_label")}
              </div>
              <div className="mt-0.5 font-display font-black text-2xl text-green-700 digit-stat">
                {t("hero.live_value")}
              </div>
              <div className="text-[11px] text-stone-500 leading-tight">
                {t("hero.live_caption")}
              </div>
            </div>
            <div className="absolute bottom-4 left-4 right-4 bg-green-700 text-white rounded-2xl p-4 shadow-lg">
              <div className="text-[11px] uppercase tracking-wider opacity-80">
                {t("hero.this_month")}
              </div>
              <div className="flex items-end justify-between gap-3">
                <div>
                  <div className="font-display font-extrabold text-2xl digit-stat">
                    ₹ 49,440
                  </div>
                  <div className="text-xs opacity-90">{t("hero.saved")}</div>
                </div>
                <div className="flex items-center gap-1 text-xs opacity-95">
                  <IndianRupee className="w-3.5 h-3.5" />
                  1.2 {t("hero.co2")}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute -top-6 -right-3 w-24 h-24 rounded-full bg-yellow-300 blur-2xl opacity-50" />
          <div className="absolute -bottom-8 -left-6 w-28 h-28 rounded-full bg-green-400 blur-2xl opacity-40" />
        </div>
      </div>
    </section>
  );
}
